import React, { useEffect, useState } from "react";
import { Alert, Box, Skeleton, Typography } from "@mui/material";
import { fetchCallTranscript } from "../gestionCarteraApi";
import { formatDate } from "../gestionCarteraFormat";
import { formatTime, parseAdvisor } from "../gestionCobranzaCatalog";
import { COLORS, labelSx, ModalShell, SecondaryButton } from "../gestionCarteraUi";

const SECONDS_PER_MINUTE = 60;

const formatDuration = (seconds) => {
  if (!seconds && seconds !== 0) return "—";
  const minutes = Math.floor(seconds / SECONDS_PER_MINUTE);
  return `${minutes}:${String(seconds % SECONDS_PER_MINUTE).padStart(2, "0")}`;
};

function Turn({ turn, agentName }) {
  const isAgent = turn.speaker === "AGENT";
  return (
    <Box sx={{ display: "flex", justifyContent: isAgent ? "flex-start" : "flex-end" }}>
      <Box sx={{ maxWidth: "80%", px: 1.75, py: 1.25, borderRadius: "12px", backgroundColor: isAgent ? "#EEF1F6" : "#E8F5EC" }}>
        <Typography sx={{ fontSize: 14, fontWeight: 700, color: COLORS.muted, mb: 0.25 }}>
          {`${isAgent ? agentName : "Cliente"} · ${formatDuration(turn.offsetSeconds)}`}
        </Typography>
        <Typography sx={{ fontSize: 14 }}>{turn.text}</Typography>
      </Box>
    </Box>
  );
}

/** Transcripción (y grabación, si backend la entrega) de una llamada de IA. Solo lectura. */
export default function TranscripcionModal({ open, management, onClose }) {
  const [resource, setResource] = useState({ status: "loading", data: null });

  useEffect(() => {
    if (!open || !management?.externalCallId) return;
    setResource({ status: "loading", data: null });
    fetchCallTranscript(management.externalCallId)
      .then((data) => setResource({ status: "ready", data }))
      .catch(() => setResource({ status: "error", data: null }));
  }, [open, management]);

  if (!management) return null;
  const advisor = parseAdvisor(management.advisorName, management.channel);
  const data = resource.data;

  return (
    <ModalShell
      open={open}
      onClose={onClose}
      title="Transcripción de la llamada"
      subtitle={`${formatDate(management.managementDate)} ${formatTime(management.managementTime)} · Crédito ${management.creditOldId}`}
      maxWidth="md"
      actions={<SecondaryButton onClick={onClose}>Cerrar</SecondaryButton>}
    >
      <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr 1fr", sm: "repeat(3, 1fr)" }, gap: 2 }}>
        <Box>
          <Typography sx={labelSx}>Agente</Typography>
          <Typography sx={{ fontWeight: 500 }}>{advisor.name}</Typography>
          {advisor.role && <Typography sx={{ fontSize: 14, color: COLORS.muted }}>{advisor.role}</Typography>}
        </Box>
        <Box>
          <Typography sx={labelSx}>Resultado</Typography>
          <Typography sx={{ fontWeight: 500 }}>{management.statusRaw?.trim() || "—"}</Typography>
        </Box>
        <Box>
          <Typography sx={labelSx}>Duración</Typography>
          <Typography sx={{ fontWeight: 500 }}>{formatDuration(data?.durationSeconds)}</Typography>
        </Box>
      </Box>

      {resource.status === "loading" && <Skeleton variant="rounded" height={260} />}
      {resource.status === "error" && <Alert severity="error">No pudimos cargar la transcripción de esta llamada.</Alert>}
      {resource.status === "ready" && (
        <>
          {data.recordingUrl && (
            <Box>
              <Typography sx={{ ...labelSx, mb: 0.75 }}>Grabación</Typography>
              <audio controls preload="none" src={data.recordingUrl} style={{ width: "100%" }}>
                Tu navegador no puede reproducir la grabación.
              </audio>
            </Box>
          )}
          {data.turns?.length ? (
            <Box sx={{ display: "flex", flexDirection: "column", gap: 1.25, maxHeight: { sm: "50vh" }, overflowY: "auto", pr: 0.5 }}>
              {data.turns.map((turn, index) => <Turn key={index} turn={turn} agentName={advisor.name} />)}
            </Box>
          ) : (
            <Alert severity="info">Esta llamada no tiene transcripción disponible.</Alert>
          )}
        </>
      )}
    </ModalShell>
  );
}
